// @flow
import React, {useEffect, useState} from "react";
import {Table, Col, Divider, Row, Modal, Button, Typography, Select, Space, Tooltip} from "antd";
import moment from "moment";
import {useHistory} from "react-router-dom";
import {
    FileSearchOutlined,
    CloseCircleOutlined,
    CheckCircleOutlined,
    ClearOutlined,
    InfoCircleOutlined
} from "@ant-design/icons";
import {useTranslation, Trans} from "react-i18next";
import {
    TableTitle,
    TableOverflow,
    DescriptionContainer,
    OrderNumber,
    OrderTotalTime,
    QueueModalContainer,
    PrepModalContainer,
    SelectField,
    PrepDetailContainer
} from "./orderDetail/orderDetailStyles";
import {OrderTable as OrderTableNew} from "./orderDetail/orderDetailStyles";
import {useStore} from "../../hooks/useStore";
import {orderTablePropType} from "./orderTablePropTypes";
import {
    getConstant,
    isConstantExist,
    truncateString,
    calculateElapsedTime
} from "../../utils/helpers";
import {deliveryStatuses, courierStatuses, courierStatusColors} from "../../constants/constants";
import {CustomButton} from "../_defaultComponents/CustomButton/CustomButton";
import {formatPhoneNumber} from "../../utils/helpers";
import {TextBoxFormModal} from "../_defaultComponents/TextBoxFormModal/TextBoxFormModal";
import ProductTable from "../_defaultComponents/ProductTable/ProductTable";
import {CourierLink} from "../_defaultComponents/CourierLink/CourierLink";
import {ConditionalTooltip} from "../_defaultComponents/ConditionalTooltip/ConditionalTooltip";
import {TableContainer} from "./ComponentStyles";
import {EmptyMessage} from "../_defaultComponents/EmptyMessage/EmptyMessage";

const {Text} = Typography;
const {Column} = Table;

const initialText = {
    label: "",
    title: ""
};

export const OrderTable = ({
    loading,
    data,
    dataCount,
    setPage,
    currentPage,
    setField,
    field,
    orderType,
    setRecall,
    recallRef
}: orderTablePropType) => {
    const history = useHistory();
    const stores = useStore();
    const {t, i18n} = useTranslation(["deliveryStatuses", "orderDetail"]);

    const [queueModalVisible, setQueueModalVisible] = useState(false);
    const [prepModalVisible, setPrepModalVisible] = useState(false);

    const [queueModalIndex, setQueueModalIndex] = useState(0);
    const [queueModalData, setQueueModalData] = useState([]);

    const [prepModalIndex, setPrepModalIndex] = useState(-1);
    const [prepModalData, setPrepModalData] = useState([]);

    const [modalVisible, setModalVisible] = useState(false);
    const [modalType, setModalType] = useState("");
    const [submit, setSubmit] = useState();
    const [deliveryType, setDeliveryType] = useState();
    const [modalData, setModalData] = useState({
        deliveryId: "",
        orderId: ""
    });
    const [text, setText] = useState(initialText);
    const [availableCouriers, setAvailableCouriers] = useState(null);
    const [preferredCourierValue, setPreferredCourierValue] = useState(null);
    const [forceCourierValue, setForceCourierValue] = useState(null);

    useEffect(() => {
        if (data?.length > 0 && data?.every(e => e.deliveryState === "inQueue")) {
            setQueueModalData(data[queueModalIndex]);
        }
    }, [data, queueModalIndex]);

    useEffect(() => {
        if (
            data?.length > 0 &&
            data?.every(e => e.deliveryState === "inPrep" || e.deliveryState === "unapproved")
        ) {
            setPrepModalData(data[prepModalIndex]);
        }
    }, [data, prepModalIndex]);

    const getAvailableCouriers = async (subCompanyId, courierType) => {
        const availableCouriers = await stores.courierStore.getAvailableCouriersForPreferredCourier(
            {
                subCompanyId,
                courierType
                //strict: true
            }
        );
        setAvailableCouriers(availableCouriers);
    };
    useEffect(() => {
        if (queueModalData && queueModalData._id) {
            setPreferredCourierValue(null);
            setForceCourierValue(null);
            getAvailableCouriers(queueModalData?.pickupInfos[0]._id, queueModalData.courierType);
        }
    }, [queueModalIndex, queueModalData]);

    const displayQueueModal = () => {
        setPreferredCourierValue(null);
        setForceCourierValue(null);
        setQueueModalVisible(true);
    };

    const displayPrepModal = () => {
        setPrepModalVisible(true);
    };

    const cancelDelivery = ({type, reason}: deliveryUpdateWithTextPropType) => {
        stores.appStore.openConfirmModal({
            title: t("modal.confirmTitle", {ns: "common"}),
            content: t("deliveryModal.cancelConfirmContent", {
                ns: "common"
            }),
            onOk: () => {
                stores.orderStore.cancelDelivery({
                    cancelText: reason,
                    deliveryId: type === "queue" ? queueModalData?._id : prepModalData?._id
                });
                setRecall(!recallRef.current);
                setModalVisible(false);
                setQueueModalVisible(false);
                setPreferredCourierValue(null);
                setForceCourierValue(null);
                setAvailableCouriers(null);
                setPrepModalVisible(false);
                setModalData({deliveryId: "", orderId: ""});
            }
        });
    };

    const approveDelivery = () => {
        stores.orderStore.approveDelivery({
            deliveryId: prepModalData?._id
        });
        setRecall(!recallRef.current);
        setModalVisible(false);
        setPreferredCourierValue(null);
        setForceCourierValue(null);
        setQueueModalVisible(false);
        setAvailableCouriers(null);
        setPrepModalVisible(false);
        setModalData({deliveryId: "", orderId: ""});
    };

    const finishPreparation = () => {
        stores.orderStore.finishPreparation({
            deliveryId: prepModalData?._id
        });
        setRecall(!recallRef.current);
        setModalVisible(false);
        setPreferredCourierValue(null);
        setForceCourierValue(null);
        setQueueModalVisible(false);
        setAvailableCouriers(null);
        setPrepModalVisible(false);
        setModalData({deliveryId: "", orderId: ""});
    };

    const queueDeliveryType =
        queueModalData?.deliveryType === "productReturn" ? "return" : "delivery";

    const calculateMultiOrderStats = delivery => {
        let totalOrdersCount = delivery?.orders?.length || 0;
        let deliveredOrdersCount = 0;
        let pickedUpOrdersCount = 0;
        if (["current", "burningOrders"].includes(orderType) && delivery?.orders.length > 1) {
            delivery?.orders?.forEach(order => {
                const hasPickedUp = delivery?.pickupInfos?.some(
                    pI =>
                        pI._id.toString() === order.pickupInfoId.toString() && pI.state === "picked"
                );
                if (hasPickedUp) {
                    pickedUpOrdersCount++;
                }

                if (order.state === "delivered") {
                    deliveredOrdersCount++;
                }
            });
        }
        return {
            totalOrdersCount,
            deliveredOrdersCount,
            pickedUpOrdersCount
        };
    };
    const renderActiveDeliveryStatusText = delivery => {
        let content = "-";

        const {
            totalOrdersCount,
            deliveredOrdersCount,
            pickedUpOrdersCount
        } = calculateMultiOrderStats(delivery);

        if (pickedUpOrdersCount > 0 || deliveredOrdersCount > 0) {
            content = (() => (
                <>
                    {t("mergedOrdersStatus.picked", {
                        ns: "orderDetail",
                        pickedUpCount: pickedUpOrdersCount,
                        totalCount: totalOrdersCount
                    })}
                    ,
                    <br />
                    {t("mergedOrdersStatus.delivered", {
                        ns: "orderDetail",
                        deliveredCount: deliveredOrdersCount,
                        totalCount: totalOrdersCount
                    })}
                </>
            ))();
        } else if (
            delivery.deliveryType === "productReturn" &&
            delivery.deliveryState === "returned"
        ) {
            content = t("delivered.name", {ns: "deliveryStatuses"});
        } else if (delivery.deliveryState === "onDeliveryAddress") {
            const endDate = delivery?.steps?.slice(-1)[0]?.endDate;
            const stepIndex =
                endDate && ["delivered", "returned"].includes(delivery?.deliveryState)
                    ? delivery.currentStepIndex + 1
                    : delivery.currentStepIndex - 1;

            const currentStep = delivery.steps[stepIndex];

            if (Boolean(currentStep.trial?.startDate) && !Boolean(currentStep.trial?.endDate)) {
                content = t("onTrial.name", {ns: "deliveryStatuses"});
            } else {
                content = t(
                    `${getConstant(deliveryStatuses, delivery.deliveryState).translation_key}.name`,
                    {
                        ns: "deliveryStatuses"
                    }
                );
            }
        } else if (delivery.deliveryState === "canceled" && delivery.merged == true) {
            content = t("mergedWithAnotherDelivery", {ns: "orderDetail"});
            if (delivery.mergedAt) {
                const deliveryLink = (
                    <a data-testid="mergedDeliveryLink" href={`/orders/${delivery.mergedAt}`} />
                );

                content = (
                    <Trans
                        i18nKey={"mergedDeliveryInfo"}
                        t={t}
                        ns="orderDetail"
                        components={{mergedDeliveryLink: deliveryLink}}
                    />
                );
            }
        } else if (isConstantExist(deliveryStatuses, delivery.deliveryState)) {
            content = t(
                `${getConstant(deliveryStatuses, delivery.deliveryState).translation_key}.name`,
                {
                    ns: "deliveryStatuses"
                }
            );
        }
        return <div data-testid="deliveryStatuses">{content}</div>;
    };

    const removePreferredCourier = () => {
        stores.orderStore.removePreferredCourier({
            deliveryId: queueModalData?._id
        });
        setRecall(recallRef.current);
        setModalVisible(false);
        setPreferredCourierValue(null);
        setForceCourierValue(null);
        setQueueModalVisible(false);
        setAvailableCouriers(null);
        setPrepModalVisible(false);
    };

    const updatePreferredCourier = () => {
        stores.orderStore.updatePreferredCourier({
            deliveryId: queueModalData?._id,
            courierId: preferredCourierValue
        });
        setRecall(recallRef.current);
        setModalVisible(false);
        setPreferredCourierValue(null);
        setForceCourierValue(null);
        setQueueModalVisible(false);
        setAvailableCouriers(null);
        setPrepModalVisible(false);
    };

    const removeForceCourier = () => {
        stores.orderStore.removeForceCourier({
            deliveryId: queueModalData?._id
        });
        setRecall(recallRef.current);
        setModalVisible(false);
        setPreferredCourierValue(null);
        setForceCourierValue(null);
        setQueueModalVisible(false);
        setAvailableCouriers(null);
        setPrepModalVisible(false);
    };

    const updateForceCourier = () => {
        stores.orderStore.updateForceCourier({
            deliveryId: queueModalData?._id,
            forceCourierId: forceCourierValue
        });
        setRecall(recallRef.current);
        setModalVisible(false);
        setPreferredCourierValue(null);
        setForceCourierValue(null);
        setQueueModalVisible(false);
        setAvailableCouriers(null);
        setPrepModalVisible(false);
    };

    const renderCompanyName = ({delivery}) => {
        let companyName = delivery.company.companyName;
        if (delivery.orders.length > 1) {
            companyName = delivery.orders.some(order => order?.pickupInfoCompanyName)
                ? delivery.orders.map(order => order?.pickupInfoCompanyName).join(", ")
                : companyName;
        }
        return (
            <ConditionalTooltip
                condition={truncateString(companyName, 40).indexOf("...") > 0}
                text={companyName}
                child={
                    <div data-testid="companyName" style={{maxWidth: "470px"}}>
                        {truncateString(companyName, 40)}
                    </div>
                }
            />
        );
    };

    const renderVigoOrderNumber = ({delivery}) => {
        let vigoOrderNumbers = delivery.orders.map(x => x.vigoOrderNumber).join(", ");
        if (delivery.orders.length > 1) {
            vigoOrderNumbers = delivery.orders.some(order => order?.vigoOrderNumber)
                ? delivery.orders
                      .filter(order => {
                          if (stores.meStore.checkRole("isCompanyUser")) {
                              return (
                                  stores.meStore.auth.companies.includes(
                                      order?.pickupInfoCompanyId?.toString()
                                  ) ||
                                  stores.meStore.auth.subcompanies.includes(
                                      order?.pickupInfoId?.toString()
                                  )
                              );
                          }
                          return true;
                      })
                      .map(order => order?.vigoOrderNumber)
                      .join(", ")
                : vigoOrderNumbers;
        }
        return (
            <ConditionalTooltip
                condition={truncateString(vigoOrderNumbers, 16).indexOf("...") > 0}
                text={vigoOrderNumbers}
                child={
                    <div data-testid="orderNumber" style={{maxWidth: "470px"}}>
                        {truncateString(vigoOrderNumbers, 16)}
                    </div>
                }
            />
        );
    };

    const renderSubCompany = ({delivery}) => {
        let subCompanyName = delivery.pickupInfos[0].name;
        if (delivery.orders.length > 1) {
            subCompanyName = delivery.orders.some(order => order?.pickupInfoName)
                ? delivery.orders
                      .filter(order => {
                          if (stores.meStore.checkRole("isCompanyUser")) {
                              return (
                                  stores.meStore.auth.companies.includes(
                                      order?.pickupInfoCompanyId?.toString()
                                  ) ||
                                  stores.meStore.auth.subcompanies.includes(
                                      order?.pickupInfoId?.toString()
                                  )
                              );
                          }
                          return true;
                      })
                      .map(order => order?.pickupInfoName)
                      .join(", ")
                : subCompanyName;
        }

        return (
            <ConditionalTooltip
                condition={truncateString(subCompanyName, 40).indexOf("...") > 0}
                text={subCompanyName}
                child={
                    <div data-testid="subCompanyName" style={{maxWidth: "470px"}}>
                        {truncateString(subCompanyName, 40)}
                    </div>
                }
            />
        );
    };

    const renderOrderNumber = ({delivery}) => {
        let orderNumbers = delivery.orders.map(x => x.orderNumber).join(", ");
        if (delivery.orders.length > 1) {
            orderNumbers = delivery.orders.some(order => order?.orderNumber)
                ? delivery.orders
                      .filter(order => {
                          if (stores.meStore.checkRole("isCompanyUser")) {
                              return (
                                  stores.meStore.auth.companies.includes(
                                      order?.pickupInfoCompanyId?.toString()
                                  ) ||
                                  stores.meStore.auth.subcompanies.includes(
                                      order?.pickupInfoId?.toString()
                                  )
                              );
                          }
                          return true;
                      })
                      .map(order => order?.orderNumber)
                      .join(", ")
                : orderNumbers;
        }
        return (
            <ConditionalTooltip
                condition={truncateString(orderNumbers, 16).indexOf("...") > 0}
                text={orderNumbers}
                child={
                    <div data-testid="orderNumber" style={{maxWidth: "470px"}}>
                        {truncateString(orderNumbers, 16)}
                    </div>
                }
            />
        );
    };

    return (
        <>
            <TextBoxFormModal
                isVisible={modalVisible}
                setVisible={() => setModalVisible(!modalVisible)}
                okFunction={submit}
                label={text.label}
                title={text.title}
                modalType={modalType}
                modalData={modalData}
                cancelFn={() => {
                    setModalType("");
                }}
                deliveryType={deliveryType}
            />
            <Modal
                width={700}
                footer={null}
                visible={queueModalVisible}
                data-testid="queueModal"
                onCancel={() => {
                    setPreferredCourierValue(null);
                    setForceCourierValue(null);
                    setQueueModalVisible(false);
                    setAvailableCouriers(null);
                }}
            >
                <QueueModalContainer>
                    <OrderNumber>
                        {t("fields.orderNumber", {ns: "common"})}:{" "}
                        <span data-testid="orderNumber">
                            {queueModalData?.orders
                                ? queueModalData.orders.map(x => x.orderNumber).join(", ")
                                : "-"}
                        </span>
                    </OrderNumber>
                    {queueModalData?.orders &&
                    queueModalData?.orders[0] &&
                    queueModalData?.orders[0].platformPickingTime ? (
                        <PrepDetailContainer>
                            <TableTitle>{t("other.platformDetail", {ns: "common"})}</TableTitle>
                            <Table
                                rowKey="_id"
                                dataSource={queueModalData?.orders}
                                pagination={false}
                            >
                                <Column
                                    title={t("other.platformPickingTime", {ns: "common"})}
                                    render={text =>
                                        moment
                                            .utc(text.platformPickingTime)
                                            .format("DD-MM-YYYY HH:mm")
                                    }
                                />
                                <Column
                                    title={t("other.platformDeliveryTime", {ns: "common"})}
                                    render={text =>
                                        moment
                                            .utc(text.platformDeliveryTime)
                                            .format("DD-MM-YYYY HH:mm")
                                    }
                                />
                                <Column
                                    title={""}
                                    render={text =>
                                        text.isPlatformSlotOrder
                                            ? t("other.platformSlotDelivery", {ns: "common"})
                                            : t("other.platfromQuickDelivery", {ns: "common"})
                                    }
                                />
                            </Table>
                        </PrepDetailContainer>
                    ) : (
                        ""
                    )}
                    <Row>
                        <div>
                            <Text type="success">{t("fields.start", {ns: "common"})}: </Text>
                            <span data-testid="startTime">
                                {moment(queueModalData?.createdAt).format("HH:mm:ss")}
                            </span>
                        </div>
                    </Row>
                    <OrderTotalTime>
                        {t("fields.elapsedTime", {ns: "common"})}:{" "}
                        <span data-testid="elapsedTime">
                            {queueModalData?.deliveryState === "canceled"
                                ? t("canceled.shortName", {
                                      ns: "deliveryStatuses"
                                  })
                                : calculateElapsedTime(queueModalData?.createdAt, new Date())}
                        </span>
                    </OrderTotalTime>
                    <TableTitle>
                        {queueModalData?.deliveryType === "productReturn"
                            ? t("fields.returnPoints", {ns: "common"})
                            : t("fields.deliveryPoints", {ns: "common"})}
                    </TableTitle>
                    <TableOverflow>
                        <OrderTableNew
                            rowKey="_id"
                            dataSource={queueModalData?.orders}
                            pagination={false}
                            data-testid={`${queueDeliveryType}PointTable`}
                            expandable={{
                                expandedRowRender: record => {
                                    return (
                                        <ProductTable
                                            showNote
                                            note={record.note}
                                            dataSource={record.products}
                                            showPaymentInfo={true}
                                            price={record.price}
                                            paymentType={record.paymentType}
                                            barcode={record.barcode}
                                            showBarcode={true}
                                        />
                                    );
                                }
                            }}
                        >
                            <Column
                                title={
                                    queueModalData?.deliveryType === "productReturn"
                                        ? t("fields.returnPoint", {
                                              ns: "common"
                                          })
                                        : t("fields.customerName", {
                                              ns: "common"
                                          })
                                }
                                data-testid={`${
                                    queueDeliveryType === "return" ? "returnPoint" : "customerName"
                                }`}
                                render={text => text.receiverName}
                            />
                            <Column
                                title={t("fields.phone", {ns: "common"})}
                                data-testid={`phone`}
                                render={text => formatPhoneNumber(text.receiverPhone)}
                            />
                            <Column
                                title={t("fields.address", {
                                    ns: "common"
                                })}
                                data-testid={`address`}
                                render={text => text.receiverAddress}
                            />
                            <Column
                                title={t("fields.addressDetail", {
                                    ns: "common"
                                })}
                                data-testid={`addressDetail`}
                                render={text => text.receiverAddressDetail}
                            />
                        </OrderTableNew>
                    </TableOverflow>

                    <TableTitle>
                        {queueModalData?.deliveryType === "productReturn"
                            ? t("fields.returnPickupPoints", {
                                  ns: "common"
                              })
                            : t("fields.pickupPoints", {ns: "common"})}
                    </TableTitle>
                    <Table
                        rowKey="_id"
                        dataSource={queueModalData?.pickupInfos}
                        pagination={false}
                        data-testid={`collectingPointTable`}
                    >
                        <Column
                            title={
                                queueModalData?.deliveryType === "productReturn"
                                    ? t("fields.customerName", {
                                          ns: "common"
                                      })
                                    : t("fields.warehouseName", {
                                          ns: "common"
                                      })
                            }
                            render={text => <span data-testid="name">{text.name}</span>}
                        />
                        <Column
                            title={t("fields.phone", {ns: "common"})}
                            render={text => (
                                <span data-testid="phoneNumber">
                                    {formatPhoneNumber(text.phone)}
                                </span>
                            )}
                        />
                        <Column
                            title={t("fields.address", {ns: "common"})}
                            render={text => <span data-testid="address">{text.address}</span>}
                        />
                    </Table>
                    {stores.meStore.checkRole("updatePreferredCourier") ? (
                        <Row>
                            <>
                                <DescriptionContainer column={2} bordered>
                                    <DescriptionContainer.Item
                                        span={2}
                                        label={t("fields.preferredCourier", {ns: "common"})}
                                    >
                                        <span data-testid="preferredCourierFullName">
                                            {queueModalData?.preferredCourier?.fullname}
                                        </span>
                                    </DescriptionContainer.Item>
                                    <DescriptionContainer.Item span={2}>
                                        <span data-testid="selectereferredCourier">
                                            <SelectField
                                                placeholder="Atanacak Kurye"
                                                name="selectedPreferredCourier"
                                                data-testid="selectedPreferredCourier"
                                                value={preferredCourierValue}
                                                onChange={value => setPreferredCourierValue(value)}
                                                loading={availableCouriers === null}
                                                disabled={availableCouriers === null}
                                            >
                                                {availableCouriers?.map(courier => {
                                                    if (
                                                        courier.id !=
                                                        queueModalData?.preferredCourier?._id
                                                    ) {
                                                        return (
                                                            <Select.Option
                                                                key={courier.id}
                                                                value={courier.id}
                                                                style={{
                                                                    color:
                                                                        courierStatusColors[
                                                                            courier.status
                                                                        ]
                                                                }}
                                                            >
                                                                {`${courier.name} - ${
                                                                    courierStatuses.find(
                                                                        c =>
                                                                            c.value ==
                                                                            courier.status
                                                                    ).name
                                                                } - ${courier.distance?.toFixed(
                                                                    1
                                                                )} km`}
                                                            </Select.Option>
                                                        );
                                                    }
                                                })}
                                            </SelectField>
                                        </span>
                                    </DescriptionContainer.Item>
                                    <DescriptionContainer.Item span={2}>
                                        <Space>
                                            <Button
                                                disabled={!preferredCourierValue}
                                                type="primary"
                                                data-testid="selectPreferredCourierButton"
                                                onClick={() => {
                                                    updatePreferredCourier();
                                                }}
                                                icon={<CheckCircleOutlined />}
                                            >
                                                {t("actions.updatePreferredCourier", {
                                                    ns: "orderDetail"
                                                })}
                                            </Button>
                                            <Button
                                                type="danger"
                                                data-testid="clearPreferredCourierButton"
                                                onClick={() => {
                                                    removePreferredCourier();
                                                }}
                                                icon={<ClearOutlined />}
                                            >
                                                {t("actions.clearPreferredCourier", {
                                                    ns: "orderDetail"
                                                })}
                                            </Button>
                                        </Space>
                                    </DescriptionContainer.Item>
                                </DescriptionContainer>
                            </>
                        </Row>
                    ) : (
                        <></>
                    )}
                    {stores.meStore.checkRole("updateForceCourier") ? (
                        <Row>
                            <>
                                <DescriptionContainer column={2} bordered>
                                    <DescriptionContainer.Item span={2} label={"Zorunlu Kurye"}>
                                        <span data-testid="preferredCourierFullName">
                                            {queueModalData?.forceCourier?.fullname}
                                        </span>
                                    </DescriptionContainer.Item>
                                    <DescriptionContainer.Item span={2}>
                                        <span data-testid="selectereferredCourier">
                                            <SelectField
                                                placeholder="Atanacak Kurye"
                                                name="selectedPreferredCourier"
                                                data-testid="selectedPreferredCourier"
                                                value={forceCourierValue}
                                                onChange={value => setForceCourierValue(value)}
                                                loading={availableCouriers === null}
                                                disabled={availableCouriers === null}
                                            >
                                                {availableCouriers?.map(courier => {
                                                    if (
                                                        courier.id !=
                                                        queueModalData?.forceCourier?._id
                                                    ) {
                                                        return (
                                                            <Select.Option
                                                                key={courier.id}
                                                                value={courier.id}
                                                                style={{
                                                                    color:
                                                                        courierStatusColors[
                                                                            courier.status
                                                                        ]
                                                                }}
                                                            >
                                                                {`${courier.name} - ${
                                                                    courierStatuses.find(
                                                                        c =>
                                                                            c.value ==
                                                                            courier.status
                                                                    ).name
                                                                } - ${courier.distance?.toFixed(
                                                                    1
                                                                )} km`}
                                                            </Select.Option>
                                                        );
                                                    }
                                                })}
                                            </SelectField>
                                        </span>
                                    </DescriptionContainer.Item>
                                    <DescriptionContainer.Item span={2}>
                                        <Space>
                                            <Button
                                                disabled={!forceCourierValue}
                                                type="primary"
                                                data-testid="selectPreferredCourierButton"
                                                onClick={() => {
                                                    updateForceCourier();
                                                }}
                                                icon={<CheckCircleOutlined />}
                                            >
                                                {"Zorunlu Kuryeyi Güncelle"}
                                            </Button>
                                            <Button
                                                type="danger"
                                                data-testid="clearPreferredCourierButton"
                                                onClick={() => {
                                                    removeForceCourier();
                                                }}
                                                icon={<ClearOutlined />}
                                            >
                                                {"Zorunlu Kuryeyi Temizle"}
                                            </Button>
                                        </Space>
                                    </DescriptionContainer.Item>
                                </DescriptionContainer>
                            </>
                        </Row>
                    ) : (
                        <></>
                    )}
                    {stores.meStore.checkRole("notVisibleForCompanyUser") && (
                        <>
                            <TableTitle>{t("fields.company", {ns: "common"})}</TableTitle>
                            <DescriptionContainer column={2} bordered>
                                <DescriptionContainer.Item
                                    span={2}
                                    label={t("fields.companyName", {
                                        ns: "common"
                                    })}
                                >
                                    <span data-testid="companyName">
                                        {queueModalData?.company?.companyName}
                                    </span>
                                </DescriptionContainer.Item>
                                <DescriptionContainer.Item
                                    label={t("fields.createdDate", {
                                        ns: "common"
                                    })}
                                >
                                    <span data-testid="createdAt">
                                        {moment(queueModalData?.company?.createdAt)
                                            .locale(i18n.resolvedLanguage || "tr")
                                            .format("ll")}
                                    </span>
                                </DescriptionContainer.Item>
                            </DescriptionContainer>
                        </>
                    )}
                    {stores.meStore.checkRole("cancelRepeatOrder") && (
                        <Row justify="end">
                            <Button
                                type="danger"
                                onClick={() => {
                                    setModalVisible(!modalVisible);
                                    setModalData({
                                        type: "queue",
                                        deliveryId: queueModalData?._id
                                    });
                                    setModalType("cancel");
                                    setText({
                                        label: t("deliveryModal.cancelReasonLabel", {
                                            ns: "common"
                                        }),
                                        title: t("deliveryModal.cancelReasonTitle", {
                                            ns: "common"
                                        })
                                    });
                                    setSubmit(() => cancelDelivery);
                                }}
                                data-testid="cancelButton"
                                icon={<CloseCircleOutlined />}
                            >
                                {t("actions.cancelOrder", {ns: "common"})}
                            </Button>
                        </Row>
                    )}
                </QueueModalContainer>
            </Modal>
            {/*@TODO: split this modal*/}
            {prepModalVisible && (
                <Modal
                    width={700}
                    footer={null}
                    visible={prepModalVisible}
                    onCancel={() => {
                        setPrepModalVisible(false);
                    }}
                >
                    <TextBoxFormModal
                        isVisible={modalVisible}
                        setVisible={() => setModalVisible(!modalVisible)}
                        okFunction={submit}
                        label={text.label}
                        title={text.title}
                        modalType={modalType}
                        modalData={modalData}
                        cancelFn={() => {
                            setModalType("");
                        }}
                        deliveryType={deliveryType}
                    />
                    <PrepModalContainer>
                        <OrderNumber>
                            {t("fields.orderNumber", {ns: "common"})}:{" "}
                            {prepModalData?.orders
                                ? prepModalData.orders.map(x => x.orderNumber).join(", ")
                                : "-"}
                        </OrderNumber>
                        {prepModalData?.orders &&
                        prepModalData?.orders[0] &&
                        prepModalData?.orders[0].platformPickingTime ? (
                            <PrepDetailContainer>
                                <TableTitle>{t("other.platformDetail", {ns: "common"})}</TableTitle>
                                <Table
                                    rowKey="_id"
                                    dataSource={prepModalData?.orders}
                                    pagination={false}
                                >
                                    <Column
                                        title={t("other.platformPickingTime", {ns: "common"})}
                                        render={text =>
                                            moment
                                                .utc(text.platformPickingTime)
                                                .format("DD-MM-YYYY HH:mm")
                                        }
                                    />
                                    <Column
                                        title={t("other.platformDeliveryTime", {ns: "common"})}
                                        render={text =>
                                            moment
                                                .utc(text.platformDeliveryTime)
                                                .format("DD-MM-YYYY HH:mm")
                                        }
                                    />
                                    <Column
                                        title={""}
                                        render={text =>
                                            text.isPlatformSlotOrder
                                                ? t("other.platformSlotDelivery", {
                                                      ns: "common"
                                                  })
                                                : t("other.platfromQuickDelivery", {
                                                      ns: "common"
                                                  })
                                        }
                                    />
                                </Table>
                            </PrepDetailContainer>
                        ) : (
                            ""
                        )}
                        <Row>
                            <div>
                                <Text type="success">{t("fields.start", {ns: "common"})}: </Text>
                                {moment(prepModalData?.createdAt).format("HH:mm:ss")}
                            </div>
                        </Row>
                        <OrderTotalTime>
                            {t("fields.elapsedTime", {ns: "common"})}:
                            {prepModalData?.deliveryState === "canceled"
                                ? t("canceled.shortName", {
                                      ns: "deliveryStatuses"
                                  })
                                : calculateElapsedTime(prepModalData?.createdAt, new Date())}
                        </OrderTotalTime>
                        <TableTitle>
                            {prepModalData?.deliveryType === "productReturn"
                                ? t("fields.returnPoints", {ns: "common"})
                                : t("fields.deliveryPoints", {
                                      ns: "common"
                                  })}
                        </TableTitle>
                        <TableOverflow>
                            <OrderTableNew
                                rowKey="_id"
                                dataSource={prepModalData?.orders}
                                pagination={false}
                                expandable={{
                                    expandedRowRender: record => {
                                        return (
                                            <>
                                                <Table
                                                    rowKey="_id"
                                                    dataSource={record.products}
                                                    pagination={false}
                                                >
                                                    <Column
                                                        dataIndex="productName"
                                                        title={t("fields.productName", {
                                                            ns: "common"
                                                        })}
                                                    ></Column>
                                                    <Column
                                                        dataIndex="ingredients"
                                                        title={t("fields.productContent", {
                                                            ns: "common"
                                                        })}
                                                    ></Column>
                                                    <Column
                                                        dataIndex="amount"
                                                        title={t("fields.productAmount", {
                                                            ns: "common"
                                                        })}
                                                    ></Column>
                                                </Table>
                                                <Col span={24}>
                                                    <Divider orientation="left">
                                                        {t("fields.orderNote", {ns: "common"})}
                                                    </Divider>
                                                    <p
                                                        style={{
                                                            marginLeft: 40,
                                                            marginRight: 40
                                                        }}
                                                    >
                                                        {record.note}
                                                    </p>
                                                </Col>
                                            </>
                                        );
                                    }
                                }}
                            >
                                <Column
                                    title={
                                        prepModalData?.deliveryType === "productReturn"
                                            ? t("fields.returnPoint", {
                                                  ns: "common"
                                              })
                                            : t("fields.customerName", {
                                                  ns: "common"
                                              })
                                    }
                                    render={text => text.receiverName}
                                ></Column>
                                <Column
                                    title={t("fields.phone", {
                                        ns: "common"
                                    })}
                                    render={text => formatPhoneNumber(text.receiverPhone)}
                                ></Column>
                                <Column
                                    title={t("fields.address", {
                                        ns: "common"
                                    })}
                                    render={text => text.receiverAddress}
                                ></Column>
                                <Column
                                    title={t("fields.addressDetail", {
                                        ns: "common"
                                    })}
                                    render={text => text.receiverAddressDetail}
                                ></Column>
                                <Column
                                    title={"Ödeme Tipi"}
                                    render={text => text.paymentType}
                                ></Column>
                            </OrderTableNew>
                        </TableOverflow>
                        <TableTitle>
                            {prepModalData?.deliveryType === "productReturn"
                                ? t("fields.returnPickupPoints", {
                                      ns: "common"
                                  })
                                : t("fields.pickupPoints", {
                                      ns: "common"
                                  })}
                        </TableTitle>
                        <Table
                            rowKey="_id"
                            dataSource={prepModalData?.pickupInfos}
                            pagination={false}
                        >
                            <Column
                                title={
                                    prepModalData?.deliveryType === "productReturn"
                                        ? t("fields.customerName", {
                                              ns: "common"
                                          })
                                        : t("fields.warehouseName", {
                                              ns: "common"
                                          })
                                }
                                render={text => text.name}
                            />
                            <Column
                                title={t("fields.phone", {ns: "common"})}
                                render={text => formatPhoneNumber(text.phone)}
                            />
                            <Column
                                title={t("fields.address", {
                                    ns: "common"
                                })}
                                render={text => text.address}
                            />
                        </Table>
                        {stores.meStore.checkRole("notVisibleForCompanyUser") && (
                            <>
                                <TableTitle>{t("fields.company", {ns: "common"})}</TableTitle>
                                <DescriptionContainer column={2} bordered>
                                    <DescriptionContainer.Item
                                        span={2}
                                        label={t("fields.companyName", {
                                            ns: "common"
                                        })}
                                    >
                                        {prepModalData?.company?.companyName}
                                    </DescriptionContainer.Item>
                                    <DescriptionContainer.Item
                                        label={t("fields.createdDate", {
                                            ns: "common"
                                        })}
                                    >
                                        {moment(prepModalData?.company?.createdAt)
                                            .locale(i18n.resolvedLanguage || "tr")
                                            .format("ll")}
                                    </DescriptionContainer.Item>
                                </DescriptionContainer>
                            </>
                        )}
                        <Row justify="end">
                            {(prepModalData?.deliveryState === "unapproved" ||
                                prepModalData?.deliveryState === "inPrep") && (
                                <Button
                                    style={{marginRight: "10px"}}
                                    type="primary"
                                    onClick={() => {
                                        setModalVisible(!modalVisible);
                                        setModalData({
                                            type: "prep",
                                            deliveryId: prepModalData?._id
                                        });
                                        setModalType("");

                                        if (prepModalData?.deliveryState === "unapproved") {
                                            setModalType("unapproved");
                                            setText({});
                                            setSubmit(() => approveDelivery);
                                        } else if (prepModalData?.deliveryState === "inPrep") {
                                            setModalType("inPrep");
                                            setText({});
                                            setSubmit(() => finishPreparation);
                                        }
                                    }}
                                    icon={
                                        prepModalData?.deliveryState === "unapproved" ||
                                        prepModalData?.deliveryState === "inPrep" ? (
                                            <CheckCircleOutlined />
                                        ) : (
                                            undefined
                                        )
                                    }
                                >
                                    {prepModalData?.deliveryState === "unapproved" &&
                                        t("actions.approveOrder", {
                                            ns: "orderPage"
                                        })}
                                    {prepModalData?.deliveryState === "inPrep" &&
                                        t("actions.assignCourier", {
                                            ns: "orderPage"
                                        })}
                                </Button>
                            )}
                            {stores.meStore.checkRole("cancelRepeatOrder") && (
                                <Button
                                    type="danger"
                                    onClick={() => {
                                        setModalVisible(!modalVisible);
                                        setModalData({
                                            type: "prep",
                                            deliveryId: prepModalData?._id
                                        });
                                        setModalType("cancel");
                                        setText({
                                            label: "Sipariş iptal sebebi",
                                            title: "Sipariş iptal"
                                        });
                                        setSubmit(() => cancelDelivery);
                                    }}
                                    icon={<CloseCircleOutlined />}
                                >
                                    {t("actions.cancelOrder", {
                                        ns: "common"
                                    })}
                                </Button>
                            )}
                        </Row>
                    </PrepModalContainer>
                </Modal>
            )}
            <TableContainer
                loading={loading}
                dataSource={data}
                rowKey="_id"
                data-testid="orderTable"
                pagination={{
                    total: dataCount,
                    current: currentPage,
                    onChange: async page => {
                        setPage(page);
                    },
                    hideOnSinglePage: true,
                    showSizeChanger: false
                }}
                size="small"
                style={{marginTop: 16}}
                locale={{
                    emptyText: <EmptyMessage />
                }}
                display={data?.length > 0 ? "grid" : "flex"}
                dataTitles={[
                    t("actions.detail", {ns: "common"}),
                    stores.meStore.checkRole("notVisibleForCompanyUser") &&
                        t("fields.company", {ns: "common"}),
                    data?.length > 0 && t("fields.orderNo", {ns: "common"}),
                    "Vigo Sipariş Numarası",
                    t("fields.createdAt", {ns: "common"}),
                    t("fields.subcompany", {ns: "common"}),
                    t("fields.customerName", {ns: "common"}),
                    orderType === "inQueue"
                        ? t("fields.preferredCourier", {ns: "common"})
                        : t("fields.courier", {ns: "common"}),
                    orderType === "inQueue" ? "Zorunlu Kurye" : undefined,
                    orderType === "inQueue" ? "Deneme Sayısı" : undefined,
                    t("fields.deliveryStatus", {ns: "common"}),
                    orderType === "current" ||
                    orderType === "burningOrders" ||
                    orderType === "inQueue" ||
                    orderType === "inPrep" ||
                    orderType === "inSlot"
                        ? t("fields.elapsedTime", {ns: "common"})
                        : t("fields.elapsedTime", {ns: "common"}),
                    orderType === "burningOrders" ? t("fields.lastActionTime", {ns: "common"}) : ""
                ].map(item => item)}
            >
                <Column
                    title={t("actions.detail", {ns: "common"})}
                    dataIndex="_id"
                    render={(text, row, index) => (
                        <CustomButton
                            data-testid="detailButton"
                            href={
                                orderType !== "inQueue" && orderType !== "inPrep"
                                    ? "/orders/" + text
                                    : null
                            }
                            onClick={event => {
                                if (orderType === "inQueue") {
                                    setQueueModalIndex(index);
                                    displayQueueModal();
                                } else if (orderType === "inPrep") {
                                    setPrepModalIndex(index);
                                    displayPrepModal();
                                } else {
                                    event.preventDefault();
                                    window.open("/orders/" + text, "_blank");
                                }
                            }}
                            icon={<FileSearchOutlined />}
                        />
                    )}
                />
                {stores.meStore.checkRole("notVisibleForCompanyUser") && (
                    <Column
                        title={t("fields.company", {ns: "common"})}
                        dataIndex="company"
                        render={(_, delivery) => (
                            <a
                                data-testid="companyName"
                                href={`/companies/${delivery?.company?._id}`}
                                target="_blank"
                            >
                                {renderCompanyName({delivery})}
                            </a>
                        )}
                    />
                )}
                <Column
                    title={t("fields.orderNo", {ns: "common"})}
                    dataIndex="currentOrderNumber"
                    render={(_, delivery) => renderOrderNumber({delivery})}
                />
                <Column
                    dataIndex="vigoOrderNumber"
                    title={"Vigo Sipariş Numarası"}
                    render={(_, delivery) => renderVigoOrderNumber({delivery})}
                />
                <Column
                    title={t("fields.createdAt", {ns: "common"})}
                    dataIndex="createdAt"
                    render={text => (
                        <div data-testid="createdAt">
                            {moment(text)
                                .locale(i18n.resolvedLanguage || "tr")
                                .format("lll")}
                        </div>
                    )}
                />
                <Column
                    title={t("fields.subcompany", {ns: "common"})}
                    dataIndex="pickupInfos"
                    render={(_, delivery) => (
                        <a
                            data-testid="subCompanyName"
                            href={`/companies/sub-company/${delivery?.pickupInfos?.[0]?._id}`}
                            target="_blank"
                        >
                            {renderSubCompany({delivery})}
                        </a>
                    )}
                />
                <Column
                    title={t("fields.customerName", {ns: "common"})}
                    dataIndex="orders"
                    render={text => (
                        <div data-testid="customerName">
                            {text && text[0].receiverName ? text[0].receiverName : "-"}
                        </div>
                    )}
                />
                {orderType === "inQueue" ? (
                    <Column
                        title={t("fields.preferredCourier", {ns: "common"})}
                        dataIndex="preferredCourier"
                        render={text => (
                            <div data-testid="courier">
                                {text && text.fullname ? text.fullname : "-"}
                            </div>
                        )}
                    />
                ) : (
                    <Column
                        title={t("fields.courier", {ns: "common"})}
                        dataIndex="assignedCourier"
                        render={courier => (
                            <div data-testid="courier">
                                {courier && stores.meStore.checkRole("courierDetail") ? (
                                    <CourierLink target="_blank" to={`/couriers/${courier?._id}`}>
                                        {courier && courier.fullname ? courier.fullname : "-"}
                                    </CourierLink>
                                ) : courier && courier.fullname ? (
                                    courier.fullname
                                ) : (
                                    "-"
                                )}
                            </div>
                        )}
                    />
                )}
                {orderType === "inQueue" ? (
                    <Column
                        title={"Zorunlu Kurye"}
                        dataIndex="forceCourier"
                        render={text => (
                            <div data-testid="courier">
                                {text && text?.fullname ? text?.fullname : "-"}
                            </div>
                        )}
                    />
                ) : (
                    undefined
                )}

                <Column
                    title={t("fields.forceCourierAssignedBy", {ns: "common"})}
                    dataIndex="forceCourierAssignedBy"
                    render={user => <div data-testid="courier">{user ? user : "-"}</div>}
                />

                {orderType === "inQueue" ? (
                    <Column
                        title={"Deneme Sayısı"}
                        dataIndex="retryCount"
                        render={text => <div data-testid="retryCount">{text}</div>}
                    />
                ) : (
                    undefined
                )}

                <Column
                    title={t("fields.deliveryStatus", {ns: "common"})}
                    dataIndex="summary"
                    render={(summary, delivery) => {
                        if (orderType === "past" && summary && summary?.types?.length !== 0) {
                            const statusesText = summary.types.map(type =>
                                t(`orderStatuses.summary.${type}`, {
                                    ns: "orderPage"
                                })
                            );
                            return (
                                <div data-testid="deliveryStatuses" style={{wordBreak: "unset"}}>
                                    {statusesText}
                                </div>
                            );
                        } else {
                            return renderActiveDeliveryStatusText(delivery);
                        }
                    }}
                />
                {orderType === "current" ||
                orderType === "burningOrders" ||
                orderType === "inQueue" ||
                orderType === "inPrep" ||
                orderType === "inSlot" ? (
                    <Column
                        sorter={() => {
                            setField({
                                field: "createdAt",
                                asc: !field.asc
                            });
                        }}
                        title={t("fields.elapsedTime", {ns: "common"})}
                        dataIndex="createdAt"
                        render={value => (
                            <div data-testid="elapsedTime">
                                {calculateElapsedTime(value, new Date())}
                            </div>
                        )}
                    />
                ) : (
                    <Column
                        title={t("fields.elapsedTime", {ns: "common"})}
                        dataIndex="steps"
                        render={(value, delivery) => {
                            const sortedSteps = value.sort((a, b) =>
                                new Date(b.stepIndex) > new Date(a.stepIndex) ? -1 : 1
                            );
                            if (delivery.summary.types.some(type => type === "autoCanceled")) {
                                return (
                                    <Tooltip
                                        title={
                                            "Siparişin oluşturulması ile iptal edilmesi arasında geçen süre."
                                        }
                                    >
                                        <div>
                                            {calculateElapsedTime(
                                                delivery.createdAt,
                                                delivery.cancelDate
                                            )}
                                        </div>
                                    </Tooltip>
                                );
                            }
                            if (delivery.merged) {
                                return (
                                    <Tooltip
                                        title={
                                            "Siparişin oluşturulması ile iptal edilmesi arasında geçen süre."
                                        }
                                    >
                                        <div>
                                            {calculateElapsedTime(
                                                delivery.createdAt,
                                                delivery.cancelDate
                                            )}
                                        </div>
                                    </Tooltip>
                                );
                            }
                            return (
                                <div data-testid="elapsedTime">
                                    {sortedSteps[sortedSteps.length - 1]?.endDate
                                        ? calculateElapsedTime(
                                              delivery.createdAt,
                                              sortedSteps[sortedSteps.length - 1].endDate
                                          )
                                        : delivery.merged
                                        ? "-"
                                        : t("canceled.shortName", {ns: "deliveryStatuses"})}
                                </div>
                            );
                        }}
                    />
                )}
                {orderType === "burningOrders" ? (
                    <Column
                        title={t("fields.lastActionTime", {ns: "common"})}
                        dataIndex="steps"
                        render={(value, delivery) => (
                            <div data-testid="elapsedTime">
                                <Tooltip
                                    title={
                                        delivery?.activeStep?.startDate
                                            ? moment(delivery?.activeStep?.startDate)
                                                  .locale(i18n.resolvedLanguage || "tr")
                                                  .format("lll")
                                            : ""
                                    }
                                >
                                    {delivery?.activeStep?.startDate ? (
                                        <Col>
                                            {moment
                                                .duration(
                                                    moment(delivery?.activeStep?.startDate).diff(
                                                        moment()
                                                    )
                                                )
                                                .locale(
                                                    i18n.resolvedLanguage === "tr" ? "tr" : "en"
                                                )
                                                .humanize(true)}{" "}
                                            <InfoCircleOutlined />
                                        </Col>
                                    ) : (
                                        ""
                                    )}
                                </Tooltip>
                            </div>
                        )}
                    />
                ) : (
                    <></>
                )}
            </TableContainer>
        </>
    );
};
